import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GameBoard } from "./components/game-board.tsx";
import { GameControls } from "./components/game-controls.tsx";
import { useGame } from "./hooks/use-game.ts";
import { getDailyPuzzle, fetchDailyPuzzle, getPuzzleNumber } from "./lib/daily.ts";
import { loadStats, saveStats, recordCompletion } from "./lib/storage.ts";
import type { GameState, Direction, Puzzle } from "./types.ts";

const DIR_CHARS: Record<Direction, string> = {
  up: "U",
  down: "D",
  left: "L",
  right: "R",
};

export function App({ onWin, embedded, winExtra, difficulty }: { onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode; difficulty?: string } = {}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const puzzleNumber = useMemo(() => getPuzzleNumber(), []);

  useEffect(() => {
    setPuzzle(null);
    fetchDailyPuzzle(difficulty)
      .then(setPuzzle)
      .catch(() => setPuzzle(getDailyPuzzle(difficulty)));
  }, [difficulty]);

  if (!puzzle) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'p-4 min-h-[300px]' : 'min-h-screen p-6'} max-w-[700px] mx-auto select-none`}>
        <div className="animate-pulse text-slate-500 text-sm font-mono">Loading puzzle...</div>
      </div>
    );
  }

  return <GameInner puzzle={puzzle} puzzleNumber={puzzleNumber} onWin={onWin} embedded={embedded} winExtra={winExtra} />;
}

function GameInner({ puzzle, puzzleNumber, onWin, embedded, winExtra }: { puzzle: Puzzle; puzzleNumber: number; onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode }) {
  const { state, move, undo, reset } = useGame(puzzle);
  const [showHelp, setShowHelp] = useState(false);
  const [stats, setStats] = useState(() => loadStats());

  const won = state.phase === "won";
  const lost = state.phase === "lost";

  useEffect(() => {
    if (won && !stats.history[puzzle.id]?.completed) {
      const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
      const updated = recordCompletion(stats, {
        puzzleId: puzzle.id,
        completed: true,
        stars: state.stars,
        moves: state.moveHistory,
        timeMs,
      });
      setStats(updated);
      saveStats(updated);
      onWin?.({ stars: state.stars, metric: state.moveHistory.length, timeMs });
    }
  }, [won]);

  return (
    <div className={`flex flex-col items-center ${embedded ? 'p-4 gap-4' : 'min-h-screen p-6 gap-6'} max-w-[700px] mx-auto select-none`}>
      {/* Header — hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <span className="text-violet-400">Lock</span>step
            </h1>
            <div className="flex gap-3 text-sm text-slate-500">
              <span>#{puzzleNumber}</span>
              <span className="text-slate-600">
                {puzzle.difficulty.name} ({puzzle.initialTokens.length} tokens)
              </span>
              {stats.streak > 0 && (
                <span className="text-violet-400">{stats.streak} day streak</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {puzzle.initialTokens.map((t) => (
                <div
                  key={t.color}
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: tokenColorToHex(t.color) }}
                />
              ))}
            </div>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white text-sm font-bold border border-slate-700"
            >
              ?
            </button>
          </div>
        </div>
      )}

      {/* Subheader when embedded */}
      {embedded && (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-slate-500">#{puzzleNumber}</span>
            <span className="font-mono text-xs text-slate-600">{puzzle.difficulty.name}</span>
            <div className="flex items-center gap-1">
              {puzzle.initialTokens.map((t) => (
                <div key={t.color} className="w-3 h-3 rounded-full" style={{ backgroundColor: tokenColorToHex(t.color) }} />
              ))}
            </div>
            {stats.streak > 0 && (
              <span className="font-mono text-xs text-[#FFD166]">{stats.streak} day streak</span>
            )}
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A1D33] text-slate-400 hover:text-white text-xs font-bold border border-[#2A2D42]"
          >
            ?
          </button>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div className="w-full bg-slate-800/80 rounded-xl p-4 text-sm text-slate-300 border border-slate-700 space-y-2">
          <p className="font-medium text-white">How to play</p>
          <p>
            Get every colored token onto its matching{" "}
            <strong className="text-violet-400">goal square</strong> (diamond
            outline).
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Press arrow keys or swipe — <strong>all tokens</strong> slide at
              once
            </li>
            <li>
              Tokens slide until they hit a{" "}
              <span className="text-white">wall</span>, the edge, or another
              token
            </li>
            <li>
              <span className="text-red-400">Holes</span> destroy tokens — avoid
              them!
            </li>
            <li>
              <span className="text-purple-400">Teleporters</span> warp tokens
              to the paired portal
            </li>
          </ol>
          <p className="text-slate-500">
            Use the D-pad buttons on mobile. Solve in fewest moves for 3 stars.
          </p>
        </div>
      )}

      {/* Game board */}
      <GameBoard
        grid={state.puzzle.grid}
        tokens={state.tokens}
        onMove={move}
        disabled={won || lost}
      />

      {/* Controls */}
      <GameControls
        moveCount={state.moveHistory.length}
        maxMoves={puzzle.maxMoves}
        optimalMoves={puzzle.optimalMoves}
        canUndo={state.moveHistory.length > 0 && !won && !lost}
        disabled={won || lost}
        onMove={move}
        onUndo={undo}
        onReset={reset}
      />

      {/* Win */}
      {won && (
        <div className="w-full bg-violet-900/40 border border-violet-700 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-4xl">
            {"\u2605".repeat(state.stars)}
            {"\u2606".repeat(3 - state.stars)}
          </div>
          <p className="text-xl font-bold text-violet-300">Puzzle Solved!</p>
          <p className="text-sm text-slate-400">
            {state.moveHistory.length} move
            {state.moveHistory.length !== 1 ? "s" : ""}
            {" \u2014 "}
            {state.stars === 3
              ? "Optimal!"
              : state.stars === 2
                ? "Great job!"
                : "You got it!"}
          </p>
          <ShareButton state={state} puzzleNumber={puzzleNumber} />
          {stats.totalPlayed > 0 && !embedded && (
            <div className="pt-3 border-t border-violet-800 text-xs text-slate-500 flex justify-center gap-6">
              <span>
                Played:{" "}
                <strong className="text-slate-300">{stats.totalPlayed}</strong>
              </span>
              <span>
                Stars:{" "}
                <strong className="text-amber-400">{stats.totalStars}</strong>
              </span>
              <span>
                Best streak:{" "}
                <strong className="text-slate-300">{stats.maxStreak}</strong>
              </span>
            </div>
          )}
          {winExtra}
        </div>
      )}

      {/* Lose */}
      {lost && (
        <div className="w-full bg-red-900/30 border border-red-800 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-xl font-bold text-red-300">Token Lost!</p>
          <p className="text-sm text-slate-400">
            A token fell into a hole. Try again?
          </p>
          <button
            onClick={reset}
            className="px-6 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

function tokenColorToHex(color: string): string {
  const map: Record<string, string> = {
    red: "#ef4444",
    blue: "#3b82f6",
    green: "#22c55e",
    yellow: "#eab308",
  };
  return map[color] ?? "#888";
}

function ShareButton({
  state,
  puzzleNumber,
}: {
  state: GameState;
  puzzleNumber: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const moveStr = state.moveHistory.map((d) => DIR_CHARS[d]).join("");
    const text = [
      `\uD83D\uDD00 Lockstep #${puzzleNumber}`,
      `${"\u2605".repeat(state.stars)}${"\u2606".repeat(3 - state.stars)} (${state.moveHistory.length} move${state.moveHistory.length !== 1 ? "s" : ""})`,
      moveStr,
    ].join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [state, puzzleNumber]);

  return (
    <button
      onClick={handleShare}
      className="px-6 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-sm font-medium transition-colors"
    >
      {copied ? "Copied!" : "Share Result"}
    </button>
  );
}
